import { ArrowLeft, Package, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, generatePath } from 'react-router-dom';

import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { ROUTES } from '../../constants/routes';
import { useProductsWithVariants } from '../../hooks/inventory/useProductsWithVariants';

function formatVariantLabel(size: string | null, color: string | null): string {
  return [size, color].filter(Boolean).join(' · ') || '—';
}

export default function ProductsList() {
  const { data, isLoading, error } = useProductsWithVariants();
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const rows = data ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const hay = `${r.product_name} ${r.sku} ${r.size ?? ''} ${r.color ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [data, query]);

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <Link
          to={ROUTES.INVENTORY_DASHBOARD}
          className="text-sm text-white/60 hover:text-white inline-flex items-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" /> Inventory
        </Link>
        <h1 className="text-2xl font-bold text-[#EBFFEE] mt-2">Products &amp; variants</h1>
        <p className="text-sm text-white/60 mt-1">
          Click a variant to view stock, velocity, and adjust its reorder threshold.
        </p>
      </div>

      <Card className="bg-[#221F1F] border-[#504A4A]">
        <CardHeader>
          <CardTitle className="text-text-primary flex items-center gap-2">
            <Package className="w-4 h-4 text-white/60" />
            All variants
            {data && (
              <span className="text-xs text-white/60 font-normal">
                ({filtered.length} of {data.length})
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative mb-4 max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
            <Input
              type="search"
              placeholder="Search product, SKU, size, color…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {isLoading ? (
            <p className="text-white/60 text-sm py-6 text-center">Loading variants…</p>
          ) : error ? (
            <p className="text-red-400 text-sm py-6 text-center">
              Could not load variants.
            </p>
          ) : filtered.length === 0 ? (
            <p className="text-text-secondary text-sm py-6 text-center">
              {data && data.length === 0
                ? 'No variants yet. Add products to start tracking inventory.'
                : 'No variants match your search.'}
            </p>
          ) : (
            <div className="divide-y divide-[#363232]">
              <div className="grid grid-cols-[1fr_140px_100px] gap-3 pb-2 text-xs font-semibold text-white/60 uppercase">
                <div>Product · variant</div>
                <div>SKU</div>
                <div className="text-right">On hand</div>
              </div>
              {filtered.map((row) => (
                <Link
                  key={row.variant_id}
                  to={generatePath(ROUTES.INVENTORY_VARIANT_DETAIL, { variantId: row.variant_id })}
                  className="grid grid-cols-[1fr_140px_100px] gap-3 py-3 text-sm hover:bg-[#2a2727] -mx-2 px-2 rounded transition-colors"
                >
                  <div className="min-w-0">
                    <div className="text-text-primary truncate">{row.product_name}</div>
                    <div className="text-xs text-white/50 truncate">
                      {formatVariantLabel(row.size, row.color)}
                    </div>
                  </div>
                  <div className="font-mono text-xs text-white/70 truncate self-center">
                    {row.sku}
                  </div>
                  <div className="text-right font-mono text-text-primary self-center">
                    {row.on_hand}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
